import React from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';

import DraggableArchiveImage from '@components/PlaylistEditor/DraggableArchiveImage';
import ModalVideo from '@components/PlaylistEditor/ModalVideo';
import PicturesArchiveHeader from '@components/PlaylistEditor/PicturesArchiveHeader';
import PicturesArchiveList from '@components/PlaylistEditor/PicturesArchiveList';
import PlaylistHeader from '@components/PlaylistEditor/PlaylistHeader';

import { usePlaylistEditor, usePlaylistEditorDispatch } from './PlaylistEditorContext';
import { PlaylistEditorContainer } from './PlaylistEditorUI';
import { getPlaceholderPictureForBook } from './PlaylistEditorUtils';
import PlaylistGrid from './PlaylistGrid';
import usePlaylistEditorData from './usePlaylistEditorData';
import SortablePlaylistItem from './SortablePlaylistItem';
import { DndContext, DragOverlay, pointerWithin } from '@dnd-kit/core';

const PlaylistEditor = ({ onBookTouched }) => {
    const { extendedFilters, playlistItems, activeItem, previewVideo } = usePlaylistEditor();

    const dispatch = usePlaylistEditorDispatch();

    usePlaylistEditorData();

    const handleDragStart = (event) => {
        if (event.active.data.current) {
            if (event.active.data.current.document) {
                //dragging from archive
                const doc = event.active.data.current.document;
                dispatch({
                    type: 'SetActiveItem',
                    value: doc,
                });
            } else {
                //dragging from playlist
                const doc = playlistItems.find((item) => `sortable-${item.ID}` === event.active.id);
                dispatch({
                    type: 'SetActiveItem',
                    value: doc,
                });
            }
        }
        //dispatch({ type: 'SetActiveId', value: event.active.id });
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        dispatch({ type: 'SetActiveItem', value: null });

        if (!over) return;

        const draggedId = active.id;
        const overId = over.id;

        if (draggedId.startsWith('draggable-')) {
            let newDocumentToAdd = null;
            let isNewDocumentPlaceholder = false;
            if (active.data && active.data.current && active.data.current.document) {
                newDocumentToAdd = { ...active.data.current.document };
            } else {
                newDocumentToAdd = { ...getPlaceholderPictureForBook() };
                isNewDocumentPlaceholder = true;
            }
            const alreadyInPlaylist =
                newDocumentToAdd.ID && playlistItems.some((item) => item.ID === newDocumentToAdd.ID);

            if (overId === 'playlist-grid') {
                //moving from archive to empty space in playlist grid
                if (!alreadyInPlaylist) {
                    dispatch({
                        type: 'AddPlaylistItem',
                        value: {
                            ID: newDocumentToAdd.ID,
                            media: isNewDocumentPlaceholder ? null : newDocumentToAdd,
                        },
                        placeholder: isNewDocumentPlaceholder,
                    });
                }
            } else if (overId.startsWith('sortable-')) {
                // Moving from archive to playlist and sorting within playlist
                if (!alreadyInPlaylist) {
                    const newIndex = playlistItems.findIndex((item) => `sortable-${item.ID}` === overId);
                    dispatch({
                        type: 'AddPlaylistItemIndex',
                        value: {
                            index: newIndex,
                            ID: newDocumentToAdd.ID,
                            media: isNewDocumentPlaceholder ? null : newDocumentToAdd,
                        },
                        placeholder: isNewDocumentPlaceholder,
                    });
                }
            }
        } else {
            // Sorting within playlist
            if (draggedId !== overId) {
                // dropping on a sortable item
                if (overId.startsWith('sortable-')) {
                    const oldIndex = playlistItems.findIndex((item) => `sortable-${item.ID}` === draggedId);
                    const newIndex = playlistItems.findIndex((item) => `sortable-${item.ID}` === overId);

                    if (oldIndex !== -1 && newIndex !== -1) {
                        // setPlaylistItems((items) =>
                        //     arrayMove(items, oldIndex, newIndex)
                        // );
                        dispatch({
                            type: 'MovePlaylistItem',
                            value: { oldIndex, newIndex },
                        });
                    }
                } else if (overId === 'playlist-grid') {
                    const oldIndex = playlistItems.findIndex((item) => `sortable-${item.ID}` === draggedId);
                    const newIndex = playlistItems.length;
                    if (oldIndex !== -1 && newIndex !== -1) {
                        dispatch({
                            type: 'MovePlaylistItem',
                            value: { oldIndex, newIndex },
                        });
                    }
                }
            }
        }
    };

    return (
        <>
            <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} collisionDetection={pointerWithin}>
                <PlaylistEditorContainer $extendedFilters={extendedFilters}>
                    <div className="playlist-editor-left">
                        <div className="playlist-editor-left-header">
                            <PlaylistHeader />
                        </div>
                        <div className="playlist-editor-left-body">
                            <PerfectScrollbar options={{ suppressScrollX: true }}>
                                <PlaylistGrid />
                            </PerfectScrollbar>
                        </div>
                    </div>
                    <div className="playlist-editor-right">
                        <div className="playlist-editor-right-header">
                            <PicturesArchiveHeader />
                        </div>
                        <div className="playlist-editor-right-body">
                            <PicturesArchiveList />
                        </div>
                    </div>
                    <DragOverlay dropAnimation={null}>
                        {activeItem ? (
                            activeItem.page ? (
                                <SortablePlaylistItem playlistItem={activeItem} drag />
                            ) : (
                                <DraggableArchiveImage document={activeItem} isInPlaylist={false} drag />
                            )
                        ) : null}
                    </DragOverlay>
                </PlaylistEditorContainer>
            </DndContext>
            {previewVideo && (
                <ModalVideo video={previewVideo} onClose={() => dispatch({ type: 'SetPreviewVideo', value: null })} />
            )}
        </>
    );
};

export default PlaylistEditor;
